import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MathJaxContext } from 'better-react-mathjax';

import AboutSources from './AboutSections/AboutSources';
import AboutAttributes from './AboutSections/Attributes/AboutAttributes';
import AboutStatsBySource from './AboutSections/AboutStatsBySource';
import AboutSchema from './AboutSections/AboutSchema';
import AboutMetrics from './AboutSections/Metrics/AboutMetrics';
import AboutOntology from './AboutSections/AboutOntology';
import AboutCiteUs from './AboutSections/AboutCiteUs';
import Loader from '../Common/Loader';

import StatisticsDiagram from './components/statistics/StatisticsDiagram';
import Changelog from './components/statistics/Changelog';
import { StatsJSON, VersionsArray, VersionInfo } from './types';
import { formatVersion, toHashLabel, compareVersions } from './helpers';
import { releasesApiService } from '../../services/releasesApiService';
import { apiReleaseToVersionsArray } from './releaseApiMappers';
import AboutSidebar from './components/elements/AboutSidebar';
import BackgroundPages from '@/global/Background/BackgroundPages';
import SharedBadge from '@/global/Badge/Badge';
import { useDocsSubsectionScroll } from './hooks/useDocsSubsectionScroll';
import SideBarLayout from '@/components/Layout/SideBarLayout';

function ContentCard({ children }: { children: React.ReactNode }) {
  return <div className="bg-white border rounded-xl p-6 mb-4">{children}</div>;
}

function AboutContent() {
  const [searchParams] = useSearchParams();
  const [statistics, setStats] = useState<StatsJSON | null>(null);
  const [apiVersions, setApiVersions] = useState<VersionsArray[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      let statsData: StatsJSON | null = null;

      try {
        const res = await fetch('/stats.json');
        statsData = res.ok ? ((await res.json()) as StatsJSON) : { current: '' };
      } catch (error) {
        console.error('❌ Error loading stats.json:', error);
        statsData = { current: '' };
      }

      if (cancelled) return;
      setStats(statsData);

      try {
        const list = await releasesApiService.getReleasesList();
        const versionKeysFromStats = new Set(Object.keys(statsData || {}).filter(k => k !== 'current'));
        const versionsToFetch = list.map(r => r.version).filter(v => !versionKeysFromStats.has(v));
        const details = await Promise.all(
          versionsToFetch.map(version => releasesApiService.getRelease(version)),
        );
        if (cancelled) return;
        const fromApi = details.map(apiReleaseToVersionsArray);
        setApiVersions(fromApi);
      } catch (error) {
        console.error('❌ Error loading releases from API:', error);
        setApiVersions([]);
      }

      if (!cancelled) setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const section = searchParams.get('section');
  useDocsSubsectionScroll(section, !loading && statistics !== null);

  if (loading || !statistics) {
    return <Loader />;
  }

  const versionsFromStats: VersionsArray[] = Object.entries(statistics)
    .filter(([key]) => key !== 'current')
    .map(([key, value]) => (typeof value === 'string' ? null : { ...(value as VersionInfo), version: key }))
    .filter((v): v is VersionsArray => v !== null);

  const statsVersionSet = new Set(versionsFromStats.map(v => v.version));
  const apiOnly = apiVersions.filter(v => !statsVersionSet.has(v.version));
  const versionsArray: VersionsArray[] = [...versionsFromStats, ...apiOnly].sort((a, b) =>
    compareVersions(b.version as string, a.version as string),
  );
  const latestVersionObj = versionsArray[0] ?? null;

  const currentVersion = versionsArray.length > 0 ? versionsArray[0].version : statistics.current || '';
  const versionParam = searchParams.get('version');

  const activeVersionObj = versionParam
    ? versionParam === 'latest'
      ? (versionsArray[0] ?? null)
      : (versionsArray.find(v => toHashLabel(v.version as string) === versionParam) ?? null)
    : (versionsArray.find(v => v.version === currentVersion) ?? versionsArray[0] ?? null);

  const showVersionDetails = Boolean(versionParam && activeVersionObj);

  if (versionsArray.length === 0) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-gray-600">No version history data available.</p>
      </section>
    );
  }

  return (
    <MathJaxContext>
      <div className="apply-trial-page">
        <BackgroundPages />
        <section className="relative z-10 px-3 sm:px-4 lg:px-12 md:mt-24 mt-20">
          <div className="docs-tools-shell home-hero-section">
            <h1 className="pt-4 mb-6">Documentation</h1>

            <div className="row txt-contenido pt-2 pb-8">
              <div className="col-12 col-lg-3 order-0 order-md-0 mb-4 mb-lg-0">
                <SideBarLayout toggleLabel="Browse documentation">
                  <div className="nav p-0 dropdown-menu-inline dropdown-menu-rounded dropdown-menu-hover-primary">
                    <AboutSidebar
                      versionsArray={versionsArray}
                      onClick={() => {}}
                      collapsed={false}
                      variant="profile"
                    />
                  </div>
                </SideBarLayout>
              </div>

              <div className="col-12 col-lg-9 order-1 order-lg-1">
                <div className="tab-content md:ml-5" id="dbInfoTabbedPane">
                  {showVersionDetails ? (
                    <ContentCard>
                      {activeVersionObj?.releaseDate && (
                        <SharedBadge className="mb-2" text={activeVersionObj.releaseDate} variant="primary" />
                      )}
                      {activeVersionObj && (
                        <h5 className="font-semibold text-2xl mb-4">
                          Release of version{' '}
                          <span className="font-bold text-pink">
                            {formatVersion(activeVersionObj.version)}
                          </span>{' '}
                          of DISGENET
                        </h5>
                      )}
                      {activeVersionObj && Object.keys(activeVersionObj.statistics).length > 0 && (
                        <StatisticsDiagram statistics={activeVersionObj.statistics} />
                      )}
                      {activeVersionObj && (
                        <Changelog
                          version={activeVersionObj.version}
                          releaseDate={activeVersionObj.releaseDate}
                          statistics={activeVersionObj.statistics}
                          changelog={activeVersionObj.changelog}
                          currentVersion={currentVersion}
                        />
                      )}
                    </ContentCard>
                  ) : (
                    <>
                      {section === 'gdaStatistics' && (
                        <ContentCard>
                          <h5 className="mb-4">Statistics</h5>
                          {latestVersionObj && <StatisticsDiagram statistics={latestVersionObj.statistics} />}
                        </ContentCard>
                      )}
                      {section === 'stats_by_source' && (
                        <ContentCard>
                          <AboutStatsBySource />
                        </ContentCard>
                      )}
                      {section === 'schema' && (
                        <ContentCard>
                          <AboutSchema />
                        </ContentCard>
                      )}
                      {section === 'sources' && (
                        <ContentCard>
                          <AboutSources />
                        </ContentCard>
                      )}
                      {section === 'metrics' && (
                        <ContentCard>
                          <AboutMetrics />
                        </ContentCard>
                      )}
                      {section === 'attributes' && (
                        <ContentCard>
                          <AboutAttributes />
                        </ContentCard>
                      )}
                      {section === 'assoctypeont' && (
                        <ContentCard>
                          <AboutOntology />
                        </ContentCard>
                      )}
                      {section === 'cite_us' && (
                        <ContentCard>
                          <AboutCiteUs />
                        </ContentCard>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </MathJaxContext>
  );
}

export default AboutContent;
